import { useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import FormField from "components/v2/FormField";
import { FiPlus } from "react-icons/fi";
import useFetchCategories from "api/CategoryResource/useFetchCategories";
import useGenres from "api/GenreResource/useGenres";
import RichTextEditor from "components/RichTextEditor";
import { MAX_IMAGE_SIZE } from "utils";
import useAddPhysicalBook from "api/BookResource/useAddPhysicalBook";

const AddPhysicalBook = () => {
	const [visible, setVisible] = useState(false);

	// Fetch data for dropdowns
	const { data: categories, isLoading: isCategoriesLoading } =
		useFetchCategories("yes");
	const { data: genres, isLoading: isGenresLoading } = useGenres("yes");

	const initialValues = {
		title: "",
		author: "",
		isbn: "",
		description: "",
		category_id: "",
		genres: "",
		stock_level: "",
		physical_usd_price: "",
		book_cover: null,
	};

	const validationSchema = Yup.object().shape({
		title: Yup.string().required("Book title is required"),
		author: Yup.string().required("Author is required"),
		category_id: Yup.number().required("Category is required"),
		genres: Yup.string().required("Genre is required"),
		stock_level: Yup.number()
			.typeError("Stock level must be a number")
			.required("Stock level is required")
			.min(0, "Stock level must be 0 or greater"),
		physical_usd_price: Yup.number()
			.typeError("Price must be a number")
			.required("USD price is required")
			.min(0, "Price must be 0 or greater"),
		book_cover: Yup.mixed()
			.required("Book cover is required")
			.test({
				name: "fileSize",
				test: (value) => !value || value.size / 1024 / 1024 <= MAX_IMAGE_SIZE,
				message: `Image size should not exceed ${MAX_IMAGE_SIZE}MB`,
			})
			.test({
				name: "mimeType",
				test: (value) =>
					!value ||
					["image/jpeg", "image/png", "image/svg+xml", "image/webp"].includes(
						value.type
					),
				message:
					"Only images with these file extensions are allowed: jpeg, jpg, png, svg, webp",
			}),
	});

	// API hook for submitting book
	const { mutateAsync, isLoading } = useAddPhysicalBook();

	const handleSubmit = async (values, { resetForm }) => {
		try {
			await mutateAsync(values);
			setVisible(false);
			resetForm();
		} catch (error) {}
	};

	const categoryOptions =
		categories?.data?.map((category) => ({
			label: category.name,
			value: category.id,
		})) || [];

	const genreOptions =
		genres?.data?.data?.map((genre) => ({
			label: genre.name,
			value: genre.id,
		})) || [];

	return (
		<>
			<Button
				label="Add physical book"
				icon={<FiPlus />}
				onClick={() => setVisible(true)}
				className="flex gap-1 rounded-lg bg-akgreener"
			/>

			<Dialog
				visible={visible}
				onHide={() => setVisible(false)}
				header="Add new physical book"
				style={{ minWidth: 600, maxWidth: 600 }}
				modal
				className="p-fluid"
				footer={
					<div className="flex justify-end gap-2">
						<Button
							label="Cancel"
							icon="pi pi-times"
							onClick={() => setVisible(false)}
							className="p-button-text"
						/>
						<Button
							label="Create Book"
							icon="pi pi-check"
							type="submit"
							form="physical-book-form"
							disabled={isLoading}
						/>
					</div>
				}
			>
				<Formik
					initialValues={initialValues}
					validationSchema={validationSchema}
					onSubmit={handleSubmit}
				>
					{({ values, errors, touched, setFieldValue }) => (
						<Form
							id="physical-book-form"
							className="space-y-6"
						>
							{/* Basic Information */}
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-6">
								<FormField
									type="text"
									name="title"
									label="Book Title *"
								/>

								<FormField
									type="text"
									name="author"
									label="Author *"
								/>
							</div>

							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								<FormField
									type="text"
									name="isbn"
									label="ISBN"
								/>

								<FormField
									type="number"
									name="stock_level"
									label="Stock Level *"
									min="0"
								/>
							</div>

							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								<FormField
									type="select"
									name="category_id"
									label="Category *"
									options={categoryOptions}
									isLoading={isCategoriesLoading}
								/>

								<FormField
									type="select"
									name="genres"
									label="Genre *"
									options={genreOptions}
									isLoading={isGenresLoading}
								/>
							</div>

							<div className="grid grid-cols-1 gap-4">
								<FormField
									type="file"
									name="book_cover"
									value={null}
									label="Book Cover *"
									style={{
										paddingLeft: `${!values?.book_cover ? "100px" : ""} `,
									}}
									onChange={(event) =>
										setFieldValue("book_cover", event.target.files[0])
									}
								/>
							</div>

							<div className="grid grid-cols-1 gap-4">
								<FormField
									type="number"
									name="physical_usd_price"
									label="USD Price *"
									min="0"
									step="0.01"
								/>
							</div>

							{/* Description */}
							<div className="space-y-2">
								<p className="text-gray-500">Book summary</p>
								<RichTextEditor
									value={values.description}
									onTextChange={(e) =>
										setFieldValue("description", e.htmlValue)
									}
									error={
										!!(touched.description && errors.description) &&
										errors.description
									}
								/>
							</div>

							{/* Book Settings */}
							{/* <div className="space-y-4">
								<h3 className="text-lg font-semibold text-gray-700 border-b pb-2">
									Book Settings
								</h3>

								<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
									<FormField
										type="switcher"
										name="is_active"
										label="Activate Book"
										switchLabelPlacement="left"
									/>

									<FormField
										type="switcher"
										name="sellable"
										label="Make Sellable"
										switchLabelPlacement="left"
									/>

									<FormField
										type="switcher"
										name="subscription"
										label="Available in Subscription"
										switchLabelPlacement="left"
									/>
								</div>
							</div> */}

							{/* Submit Button */}
						</Form>
					)}
				</Formik>
			</Dialog>
		</>
	);
};

export default AddPhysicalBook;
